/**
 * Smart Prospect Question System - Question Data
 * 
 * Contains all 88 curated questions from prospect-questions.md
 * with structured categorization and weighted selection data.
 */

export interface ProspectQuestion {
  id: number;
  text: string;
  category: QuestionCategory;
  weight: 1 | 2 | 3 | 4 | 5; // Selection weight (5 = highest priority)
  tags: string[];
  source: string;
  contextHints?: string[];
}

export enum QuestionCategory {
  INITIAL_UNDERSTANDING = 'initial',
  EXISTING_SOLUTIONS = 'objections', 
  FAMILY_SITUATIONS = 'family',
  COST_VALUE = 'cost',
  PROCESS_ELIGIBILITY = 'process',
  CLAIMS_PAYOUT = 'claims',
  SKEPTICISM_TRUST = 'skepticism',
  COMPARISONS = 'competitive',
  TECHNICAL_LEGAL = 'technical',
  EDGE_CASES = 'edge-cases'
}

/**
 * All 88 curated prospect questions with proper categorization and weights
 * 
 * Weight Distribution:
 * - Weight 5 (High Priority): 17 questions (~19%) - Core concepts, critical objections
 * - Weight 4 (Important): 26 questions (~30%) - Common objections, value propositions  
 * - Weight 3 (Standard): 26 questions (~30%) - Moderate frequency questions
 * - Weight 2 (Lower Priority): 13 questions (~15%) - Specialized scenarios
 * - Weight 1 (Rare): 6 questions (~7%) - Edge cases, very specific situations
 */
export const PROSPECT_QUESTIONS: ProspectQuestion[] = [
  // Initial Understanding & Need Questions (Weight 5 - Core BLP concepts)
  {
    id: 1,
    text: "Why do I need this? My family can just use my life insurance.",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["life_insurance", "family", "need", "objection"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["early_conversation", "basic_objection"]
  },
  {
    id: 2,
    text: "What exactly is a Beneficiary Liquidity Plan?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["definition", "blp", "concept", "explanation"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["early_conversation", "education"]
  },
  {
    id: 3,
    text: "How is this different from regular life insurance?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["comparison", "life_insurance", "difference", "value_prop"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["differentiation", "value_proposition"]
  },
  {
    id: 4,
    text: "Don't my kids already inherit everything in my will?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["inheritance", "will", "probate", "misconception"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["probate_education", "timing_issue"]
  },
  {
    id: 5,
    text: "I have a trust - doesn't that solve this problem?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["trust", "estate_planning", "liquidity", "objection"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["high_net_worth", "estate_planning"]
  },
  {
    id: 6,
    text: "What happens when someone dies that makes this necessary?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["death_process", "problem_awareness", "necessity"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["problem_identification", "education"]
  },
  {
    id: 7,
    text: "How long does it actually take to get money from life insurance?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["timing", "life_insurance", "process", "delay"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["timing_concern", "process_education"]
  },
  {
    id: 8,
    text: "What's wrong with the way things work now?",
    category: QuestionCategory.INITIAL_UNDERSTANDING,
    weight: 5,
    tags: ["status_quo", "problem", "current_system"],
    source: "Initial Understanding & Need Questions",
    contextHints: ["problem_awareness", "status_quo_challenge"]
  },

  // Existing Solutions Objections (Weight 4 - Common objections)
  {
    id: 9,
    text: "My kids are joint owners on my bank accounts - they can access money immediately.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 5, // Very common objection, elevated to weight 5
    tags: ["joint_accounts", "bank", "access", "objection"],
    source: "Existing Solutions Objections",
    contextHints: ["banking_solution", "common_objection"]
  },
  {
    id: 10,
    text: "I have power of attorney set up for my son.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 4,
    tags: ["power_of_attorney", "legal", "misconception"],
    source: "Existing Solutions Objections",
    contextHints: ["legal_misconception", "estate_planning"]
  },
  {
    id: 11,
    text: "My accounts are all set up with TOD (Transfer on Death).",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 5, // Very common estate planning tool
    tags: ["tod", "transfer_on_death", "estate_planning"],
    source: "Existing Solutions Objections",
    contextHints: ["estate_planning", "banking"]
  },
  {
    id: 12,
    text: "I already prepaid for my funeral, so this isn't needed.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 4,
    tags: ["prepaid_funeral", "objection", "partial_solution"],
    source: "Existing Solutions Objections",
    contextHints: ["funeral_planning", "partial_solution"]
  },
  {
    id: 13,
    text: "My executor can handle all of this.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 4,
    tags: ["executor", "legal", "misconception"],
    source: "Existing Solutions Objections",
    contextHints: ["legal_misconception", "executor_role"]
  },
  {
    id: 14,
    text: "Can't they just assign my existing life insurance to the funeral home?",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 3,
    tags: ["assignment", "funeral_home", "life_insurance"],
    source: "Existing Solutions Objections",
    contextHints: ["technical_solution", "funeral_process"]
  },
  {
    id: 15,
    text: "My spouse can access our joint accounts when I die.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 4,
    tags: ["spouse", "joint_accounts", "misconception"],
    source: "Existing Solutions Objections",
    contextHints: ["spousal_access", "banking"]
  },
  {
    id: 16,
    text: "I have plenty of money in checking - my family can use that.",
    category: QuestionCategory.EXISTING_SOLUTIONS,
    weight: 4,
    tags: ["checking_account", "liquidity", "misconception"],
    source: "Existing Solutions Objections",
    contextHints: ["liquid_assets", "banking"]
  },

  // Family Situation Objections (Weight 3 - Family dynamics)
  {
    id: 17,
    text: "My kids have plenty of money - they don't need this.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 3,
    tags: ["wealthy_family", "objection", "need"],
    source: "Family Situation Objections",
    contextHints: ["wealthy_family", "burden_relief"]
  },
  {
    id: 18,
    text: "My son is a doctor and my daughter is a lawyer - they can afford it.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 3,
    tags: ["professional_success", "affordability", "objection"],
    source: "Family Situation Objections",
    contextHints: ["professional_families", "burden_relief"]
  },
  {
    id: 19,
    text: "My beneficiaries are inheriting enough money already.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 3,
    tags: ["inheritance", "amount", "objection"],
    source: "Family Situation Objections",
    contextHints: ["inheritance_size", "reallocation"]
  },
  {
    id: 20,
    text: "My children live locally - they won't have travel expenses.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["local_family", "travel", "expenses"],
    source: "Family Situation Objections",
    contextHints: ["geographic", "expense_types"]
  },
  {
    id: 21,
    text: "We're a close family - they'll figure it out.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 3,
    tags: ["family_harmony", "assumption", "stress"],
    source: "Family Situation Objections",
    contextHints: ["family_dynamics", "stress_prevention"]
  },
  {
    id: 22,
    text: "I don't want to burden my kids with more inheritance taxes.",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 3,
    tags: ["inheritance_tax", "misconception", "burden"],
    source: "Family Situation Objections",
    contextHints: ["tax_misconception", "education"]
  },

  // Cost & Value Concerns (Weight 4-5 - Universal concerns)
  {
    id: 23,
    text: "How much does this cost?",
    category: QuestionCategory.COST_VALUE,
    weight: 5, // Universal concern - elevated to weight 5
    tags: ["cost", "price", "universal"],
    source: "Cost & Value Concerns",
    contextHints: ["pricing", "universal_concern"]
  },
  {
    id: 24,
    text: "Am I buying more life insurance?",
    category: QuestionCategory.COST_VALUE,
    weight: 4,
    tags: ["product_confusion", "life_insurance", "clarification"],
    source: "Cost & Value Concerns",
    contextHints: ["product_education", "clarification"]
  },
  {
    id: 25,
    text: "Do I have to make monthly premium payments?",
    category: QuestionCategory.COST_VALUE,
    weight: 4,
    tags: ["premiums", "payments", "structure"],
    source: "Cost & Value Concerns",
    contextHints: ["payment_structure", "single_premium"]
  },
  {
    id: 26,
    text: "What if I can't afford the full amount you're suggesting?",
    category: QuestionCategory.COST_VALUE,
    weight: 3,
    tags: ["affordability", "budget", "flexibility"],
    source: "Cost & Value Concerns",
    contextHints: ["affordability", "flexibility"]
  },
  {
    id: 27,
    text: "Is this just another way for insurance companies to make money?",
    category: QuestionCategory.COST_VALUE,
    weight: 4,
    tags: ["skepticism", "motives", "insurance_industry"],
    source: "Cost & Value Concerns",
    contextHints: ["skepticism", "industry_motives"]
  },
  {
    id: 28,
    text: "What's the minimum amount I can do?",
    category: QuestionCategory.COST_VALUE,
    weight: 3,
    tags: ["minimum", "budget", "entry_level"],
    source: "Cost & Value Concerns",
    contextHints: ["budget_constraints", "minimums"]
  },
  {
    id: 29,
    text: "Can I get my money back if I change my mind?",
    category: QuestionCategory.COST_VALUE,
    weight: 3,
    tags: ["refund", "commitment", "flexibility"],
    source: "Cost & Value Concerns",
    contextHints: ["commitment_concern", "flexibility"]
  },

  // Process & Eligibility Questions (Weight 3-4 - Implementation phase)
  {
    id: 30,
    text: "Do I need a medical exam?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 4,
    tags: ["medical_exam", "underwriting", "qualification"],
    source: "Process & Eligibility Questions",
    contextHints: ["qualification", "ease_of_process"]
  },
  {
    id: 31,
    text: "What if I'm already 85 years old?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 3,
    tags: ["age", "eligibility", "seniors"],
    source: "Process & Eligibility Questions",
    contextHints: ["age_eligibility", "seniors"]
  },
  {
    id: 32,
    text: "I'm in a nursing home - can I still qualify?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 2,
    tags: ["nursing_home", "health_status", "qualification"],
    source: "Process & Eligibility Questions",
    contextHints: ["health_status", "institutional_care"]
  },
  {
    id: 33,
    text: "How long does the application process take?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 3,
    tags: ["timeline", "application", "process"],
    source: "Process & Eligibility Questions",
    contextHints: ["process_timeline", "efficiency"]
  },
  {
    id: 34,
    text: "What happens if I move to another state?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 2,
    tags: ["portability", "relocation", "state_changes"],
    source: "Process & Eligibility Questions",
    contextHints: ["portability", "nationwide"]
  },
  {
    id: 35,
    text: "Do I have to pick a specific funeral home?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 4,
    tags: ["funeral_home", "flexibility", "choice"],
    source: "Process & Eligibility Questions",
    contextHints: ["flexibility", "funeral_choice"]
  },
  {
    id: 36,
    text: "What if my family chooses cremation instead of burial?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 3,
    tags: ["cremation", "burial", "service_type"],
    source: "Process & Eligibility Questions",
    contextHints: ["service_types", "flexibility"]
  },

  // Claims & Payout Concerns (Weight 4-5 - Core differentiator)
  {
    id: 37,
    text: "How do you pay claims without a death certificate? That sounds suspicious.",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 5, // Core differentiator - elevated to weight 5
    tags: ["death_certificate", "claims", "skepticism", "process"],
    source: "Claims & Payout Concerns",
    contextHints: ["core_differentiator", "skepticism"]
  },
  {
    id: 38,
    text: "What if the funeral home won't work with you?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 3,
    tags: ["funeral_home", "process", "concern"],
    source: "Claims & Payout Concerns",
    contextHints: ["process_concern", "funeral_relationships"]
  },
  {
    id: 39,
    text: "How do I know my family will actually get the money quickly?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 5, // Elevated - proof/trust critical for conversion
    tags: ["trust", "proof", "timing", "reliability"],
    source: "Claims & Payout Concerns",
    contextHints: ["proof_needed", "trust_building"]
  },
  {
    id: 40,
    text: "What if there are multiple beneficiaries?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 3,
    tags: ["multiple_beneficiaries", "distribution", "process"],
    source: "Claims & Payout Concerns",
    contextHints: ["beneficiary_management", "distribution"]
  },
  {
    id: 41,
    text: "What states don't allow this fast payout?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 2,
    tags: ["state_restrictions", "geographic", "limitations"],
    source: "Claims & Payout Concerns",
    contextHints: ["geographic_limitations", "state_specific"]
  },
  {
    id: 42,
    text: "What if I die in another country?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 1,
    tags: ["international", "death", "edge_case"],
    source: "Claims & Payout Concerns",
    contextHints: ["international", "edge_case"]
  },
  {
    id: 43,
    text: "How does the funeral home know I died?",
    category: QuestionCategory.CLAIMS_PAYOUT,
    weight: 3,
    tags: ["process", "funeral_home", "mechanics"],
    source: "Claims & Payout Concerns",
    contextHints: ["process_mechanics", "education"]
  },

  // Skepticism & Trust Issues (Weight 4-5 - Credibility building)
  {
    id: 44,
    text: "This sounds too good to be true - what's the catch?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 5, // Universal skepticism - elevated to weight 5
    tags: ["skepticism", "catch", "trust", "universal"],
    source: "Skepticism & Trust Issues",
    contextHints: ["universal_skepticism", "trust_building"]
  },
  {
    id: 45,
    text: "Why haven't I heard of this before?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 4,
    tags: ["awareness", "market_education", "legitimacy"],
    source: "Skepticism & Trust Issues",
    contextHints: ["market_awareness", "education"]
  },
  {
    id: 46,
    text: "Is this a real insurance company?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 4,
    tags: ["legitimacy", "insurance_company", "credibility"],
    source: "Skepticism & Trust Issues",
    contextHints: ["legitimacy_concern", "credibility"]
  },
  {
    id: 47,
    text: "What if your company goes out of business?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 3,
    tags: ["company_stability", "risk", "concern"],
    source: "Skepticism & Trust Issues",
    contextHints: ["company_stability", "risk_concern"]
  },
  {
    id: 48,
    text: "Can you prove this actually works?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 4,
    tags: ["proof", "evidence", "track_record"],
    source: "Skepticism & Trust Issues",
    contextHints: ["proof_request", "track_record"]
  },
  {
    id: 49,
    text: "Why don't banks or other insurance companies offer this?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 4,
    tags: ["market_positioning", "competition", "uniqueness"],
    source: "Skepticism & Trust Issues",
    contextHints: ["market_positioning", "competitive_advantage"]
  },
  {
    id: 50,
    text: "Is this some kind of scam?",
    category: QuestionCategory.SKEPTICISM_TRUST,
    weight: 4,
    tags: ["scam", "fraud", "legitimacy"],
    source: "Skepticism & Trust Issues",
    contextHints: ["fraud_concern", "legitimacy"]
  },

  // Comparison & Alternative Questions (Weight 3-4 - Competitive positioning)
  {
    id: 51,
    text: "How is this better than just saving money in a bank account?",
    category: QuestionCategory.COMPARISONS,
    weight: 5, // Elevated - very common alternative
    tags: ["bank_account", "savings", "comparison"],
    source: "Comparison & Alternative Questions",
    contextHints: ["simple_alternative", "banking"]
  },
  {
    id: 52,
    text: "What about putting money in a CD or money market?",
    category: QuestionCategory.COMPARISONS,
    weight: 3,
    tags: ["cd", "money_market", "investment_alternative"],
    source: "Comparison & Alternative Questions",
    contextHints: ["investment_alternatives", "fixed_income"]
  },
  {
    id: 53,
    text: "Why not just get more regular life insurance?",
    category: QuestionCategory.COMPARISONS,
    weight: 5, // Elevated - direct insurance alternative
    tags: ["life_insurance", "comparison", "alternative"],
    source: "Comparison & Alternative Questions",
    contextHints: ["insurance_alternative", "timing_difference"]
  },
  {
    id: 54,
    text: "Can't I just give my kids money now while I'm alive?",
    category: QuestionCategory.COMPARISONS,
    weight: 3,
    tags: ["gifting", "living_transfer", "alternative"],
    source: "Comparison & Alternative Questions",
    contextHints: ["gift_strategy", "control_concern"]
  },
  {
    id: 55,
    text: "What about funeral insurance - isn't that the same thing?",
    category: QuestionCategory.COMPARISONS,
    weight: 5, // Elevated - direct competitor question
    tags: ["funeral_insurance", "competitor", "comparison"],
    source: "Comparison & Alternative Questions",
    contextHints: ["direct_competitor", "product_comparison"]
  },
  {
    id: 56,
    text: "How does this compare to pre-paying my funeral?",
    category: QuestionCategory.COMPARISONS,
    weight: 3,
    tags: ["prepaid_funeral", "comparison", "scope"],
    source: "Comparison & Alternative Questions",
    contextHints: ["prepaid_comparison", "scope_difference"]
  },

  // Technical & Legal Questions (Weight 2-3 - Specialized knowledge)
  {
    id: 57,
    text: "What happens if I die by suicide?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["suicide", "exclusions", "policy_terms"],
    source: "Technical & Legal Questions",
    contextHints: ["policy_exclusions", "sensitive_topic"]
  },
  {
    id: 58,
    text: "Does this affect my estate taxes?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 3,
    tags: ["estate_tax", "tax_implications", "planning"],
    source: "Technical & Legal Questions",
    contextHints: ["tax_planning", "estate_impact"]
  },
  {
    id: 59,
    text: "Will this money count against Medicaid eligibility?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["medicaid", "eligibility", "asset_protection"],
    source: "Technical & Legal Questions",
    contextHints: ["medicaid_planning", "asset_protection"]
  },
  {
    id: 60,
    text: "What if there's a dispute about my death?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["dispute", "death", "legal_issue"],
    source: "Technical & Legal Questions",
    contextHints: ["legal_disputes", "process_integrity"]
  },
  {
    id: 61,
    text: "Can creditors take this money?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 3,
    tags: ["creditors", "asset_protection", "legal"],
    source: "Technical & Legal Questions",
    contextHints: ["asset_protection", "creditor_protection"]
  },
  {
    id: 62,
    text: "What if I get divorced and want to change beneficiaries?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["divorce", "beneficiary_change", "life_events"],
    source: "Technical & Legal Questions",
    contextHints: ["life_changes", "beneficiary_management"]
  },
  {
    id: 63,
    text: "Does this go through probate?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 3,
    tags: ["probate", "estate_settlement", "legal"],
    source: "Technical & Legal Questions",
    contextHints: ["probate_avoidance", "estate_planning"]
  },

  // State-Specific Concerns (Weight 2 - Geographic specific)
  {
    id: 64,
    text: "I live in New York - don't death certificates take forever there?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["new_york", "death_certificates", "state_specific"],
    source: "State-Specific Concerns",
    contextHints: ["state_specific", "processing_delays"]
  },
  {
    id: 65,
    text: "What if I die in a state that requires death certificates?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["state_requirements", "death_certificates", "exceptions"],
    source: "State-Specific Concerns",
    contextHints: ["state_exceptions", "process_variations"]
  },
  {
    id: 66,
    text: "I live in Indiana - can I still get fast payout?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["indiana", "state_exception", "fast_payout"],
    source: "State-Specific Concerns",
    contextHints: ["state_exceptions", "indiana_specific"]
  },
  {
    id: 67,
    text: "What if my family lives in different states?",
    category: QuestionCategory.TECHNICAL_LEGAL,
    weight: 2,
    tags: ["multi_state", "family_distribution", "geographic"],
    source: "State-Specific Concerns",
    contextHints: ["geographic_distribution", "nationwide_coverage"]
  },

  // Timing & Implementation Questions (Weight 2-3 - Implementation phase)
  {
    id: 68,
    text: "When does coverage start?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 2,
    tags: ["coverage_start", "timing", "effective_date"],
    source: "Timing & Implementation Questions",
    contextHints: ["coverage_timing", "policy_activation"]
  },
  {
    id: 69,
    text: "What if I die shortly after buying this?",
    category: QuestionCategory.PROCESS_ELIGIBILITY,
    weight: 2,
    tags: ["early_death", "waiting_period", "timing"],
    source: "Timing & Implementation Questions",
    contextHints: ["early_death_concern", "waiting_periods"]
  },
  {
    id: 70,
    text: "Can I add more money to this later?",
    category: QuestionCategory.COST_VALUE,
    weight: 2,
    tags: ["additional_funding", "stacking", "expansion"],
    source: "Timing & Implementation Questions",
    contextHints: ["policy_expansion", "additional_coverage"]
  },
  {
    id: 71,
    text: "What if funeral costs have gone up by the time I die?",
    category: QuestionCategory.COST_VALUE,
    weight: 2,
    tags: ["inflation", "funeral_costs", "future_expenses"],
    source: "Timing & Implementation Questions",
    contextHints: ["inflation_concern", "cost_projection"]
  },
  {
    id: 72,
    text: "How often should I review this amount?",
    category: QuestionCategory.COST_VALUE,
    weight: 2,
    tags: ["review", "maintenance", "updates"],
    source: "Timing & Implementation Questions",
    contextHints: ["policy_maintenance", "periodic_review"]
  },

  // Family Dynamic Concerns (Weight 2-3 - Complex family situations)
  {
    id: 73,
    text: "What if my kids fight over who gets what?",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["family_conflicts", "distribution", "arguments"],
    source: "Family Dynamic Concerns",
    contextHints: ["family_conflicts", "clear_distribution"]
  },
  {
    id: 74,
    text: "My family doesn't get along - will this cause problems?",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["family_discord", "problems", "stress"],
    source: "Family Dynamic Concerns",
    contextHints: ["family_dysfunction", "stress_reduction"]
  },
  {
    id: 75,
    text: "What if one of my beneficiaries dies before me?",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["beneficiary_death", "contingency", "updates"],
    source: "Family Dynamic Concerns",
    contextHints: ["contingent_beneficiaries", "policy_updates"]
  },
  {
    id: 76,
    text: "I have a blended family - how does this work?",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["blended_family", "complex_family", "distribution"],
    source: "Family Dynamic Concerns",
    contextHints: ["blended_families", "complex_distribution"]
  },
  {
    id: 77,
    text: "What if my executor and beneficiaries are different people?",
    category: QuestionCategory.FAMILY_SITUATIONS,
    weight: 2,
    tags: ["executor", "beneficiaries", "roles"],
    source: "Family Dynamic Concerns",
    contextHints: ["role_separation", "estate_management"]
  },

  // Edge Cases & What-If Scenarios (Weight 1 - Very specific situations)
  {
    id: 78,
    text: "What if I never have a funeral?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["no_funeral", "edge_case", "alternative_process"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["edge_case", "no_funeral"]
  },
  {
    id: 79,
    text: "What if my body is never found?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["missing_body", "edge_case", "unusual"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["edge_case", "missing_person"]
  },
  {
    id: 80,
    text: "What if I want to donate my body to science?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["body_donation", "science", "memorial"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["body_donation", "memorial_service"]
  },
  {
    id: 81,
    text: "What happens if the funeral home goes out of business?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["funeral_home_closure", "business_failure", "alternatives"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["business_continuity", "alternatives"]
  },
  {
    id: 82,
    text: "What if natural disaster prevents normal funeral services?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["natural_disaster", "emergency", "disruption"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["emergency_situations", "service_disruption"]
  },
  {
    id: 83,
    text: "What if I die during a pandemic when funeral rules change?",
    category: QuestionCategory.EDGE_CASES,
    weight: 1,
    tags: ["pandemic", "rule_changes", "emergency"],
    source: "Edge Cases & What-If Scenarios",
    contextHints: ["pandemic", "emergency_protocols"]
  },

  // Competitive & Market Questions (Weight 2 - Industry knowledge)
  {
    id: 84,
    text: "Why doesn't my current financial advisor offer this?",
    category: QuestionCategory.COMPARISONS,
    weight: 2,
    tags: ["advisor", "distribution", "availability"],
    source: "Competitive & Market Questions",
    contextHints: ["advisor_awareness", "distribution_channels"]
  },
  {
    id: 85,
    text: "What other companies sell similar products?",
    category: QuestionCategory.COMPARISONS,
    weight: 2,
    tags: ["competitors", "market", "alternatives"],
    source: "Competitive & Market Questions",
    contextHints: ["competitive_landscape", "market_uniqueness"]
  },
  {
    id: 86,
    text: "Is this available through my bank?",
    category: QuestionCategory.COMPARISONS,
    weight: 2,
    tags: ["bank", "distribution", "availability"],
    source: "Competitive & Market Questions",
    contextHints: ["distribution_channels", "bank_availability"]
  },
  {
    id: 87,
    text: "Why isn't this more widely advertised?",
    category: QuestionCategory.COMPARISONS,
    weight: 2,
    tags: ["marketing", "awareness", "distribution"],
    source: "Competitive & Market Questions",
    contextHints: ["market_awareness", "specialized_distribution"]
  },
  {
    id: 88,
    text: "What makes your company special?",
    category: QuestionCategory.COMPARISONS,
    weight: 2,
    tags: ["differentiation", "company", "competitive_advantage"],
    source: "Competitive & Market Questions",
    contextHints: ["company_differentiation", "competitive_advantage"]
  }
];

/**
 * Get questions by category
 */
export function getQuestionsByCategory(category: QuestionCategory): ProspectQuestion[] {
  return PROSPECT_QUESTIONS.filter(q => q.category === category);
}

/**
 * Get questions by weight
 */
export function getQuestionsByWeight(weight: number): ProspectQuestion[] {
  return PROSPECT_QUESTIONS.filter(q => q.weight === weight);
}

/**
 * Get question by ID
 */
export function getQuestionById(id: number): ProspectQuestion | null {
  return PROSPECT_QUESTIONS.find(q => q.id === id) || null;
}

/**
 * Weight distribution statistics for validation
 */
export const WEIGHT_DISTRIBUTION = {
  weight5: PROSPECT_QUESTIONS.filter(q => q.weight === 5).length, // Should be ~17 (19%)
  weight4: PROSPECT_QUESTIONS.filter(q => q.weight === 4).length, // Should be ~26 (30%)
  weight3: PROSPECT_QUESTIONS.filter(q => q.weight === 3).length, // Should be ~26 (30%)
  weight2: PROSPECT_QUESTIONS.filter(q => q.weight === 2).length, // Should be ~13 (15%)
  weight1: PROSPECT_QUESTIONS.filter(q => q.weight === 1).length, // Should be ~6 (7%)
  total: PROSPECT_QUESTIONS.length // Should be 88
};